import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, Box } from '@mui/material';
import { useApiContext } from '@/contexts/apiContext';
import { useTranslation } from 'react-i18next';
import ErrorMessage from '@/components/errorMessage';
import DataGridTable from '@/components/datagridTable';
import { GridColDef } from '@mui/x-data-grid';
import { format } from 'date-fns';

type Props = { entity: any; maxHeight?: number };

const auditCache: Record<string, any[]> = {};
const fetchGuard: Record<string, boolean> = {};

const AuditRecords = ({ entity, maxHeight }: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();

  const [auditRecords, setAuditRecords] = useState<any[]>(auditCache[entity?.id] || []);
  const [loading, setLoading] = useState<boolean>(!auditCache[entity?.id]);
  const [error, setError] = useState<any>();

  const dataTimeFormat = import.meta.env.VITE_DATA_TIME_FORMAT1 || 'yyyy-MM-dd HH:mm:ss';

  useEffect(() => {
    const getEntityAuditRecords = async () => {
      if (!entity?.id || fetchGuard[entity.id]) return;
      fetchGuard[entity.id] = true;

      setError(null);
      if (auditCache[entity.id]) {
        setLoading(false);
      } else {
        setLoading(true);
      }

      try {
        const records = await apiContext.getEntityAuditRecords(entity.entityType, entity.entityDefId, entity.id);
        auditCache[entity.id] = records || [];
        setAuditRecords(records || []);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
        fetchGuard[entity.id] = false;
      }
    };

    if (entity?.id) {
      getEntityAuditRecords();
    }
  }, [entity.id, entity.entityType, entity.entityDefId]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'performedTime',
        headerName: t('entity.administration.auditRecords.columns.datatime'),
        width: 200,
        valueGetter: (value: any) => {
          if (!value) return '';
          try {
            return format(new Date(value), dataTimeFormat);
          } catch (e) {
            return value;
          }
        },
      },
      { field: 'performedBy', headerName: t('entity.administration.auditRecords.columns.user'), flex: 1 },
      { field: 'action', headerName: t('entity.administration.auditRecords.columns.action') },
      {
        field: 'entityType',
        flex: 1,
        headerName: t('entity.administration.auditRecords.columns.recordType'),
        valueGetter: (value: any, row: any) => {
          return row ? t(`common.entityType.${row.entityType}`) : '';
        },
      },
      {
        field: 'entitySubType',
        flex: 1,
        headerName: t('entity.administration.auditRecords.columns.recordDefId'),
        valueGetter: (value: any, row: any) => {
          return row?.entityDefId || '';
        },
      },
      {
        field: 'entityId',
        flex: 1,
        headerName: t('entity.administration.auditRecords.columns.recordId'),
        valueGetter: (value: any, row: any) => {
          return row?.entityId || '';
        },
      },
    ],
    [t, dataTimeFormat],
  );

  return (
    <Card
      variant="outlined"
      sx={{
        height: maxHeight ? `${maxHeight}px` : '100%',
        border: 1,
        borderColor: 'divider',
        boxShadow: 'none',
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <CardContent sx={{ p: 0, height: '100%' }}>
        {error && (
          <Box sx={{ p: 1 }}>
            <ErrorMessage error={error} />
          </Box>
        )}
        <DataGridTable rows={auditRecords || []} columns={columns} loading={loading} />
      </CardContent>
    </Card>
  );
};

export default AuditRecords;
